import type { Message } from "../interfaces";
import type {
  SelfHelpFeedbackFormAssociation,
  SelfHelpFeedbackMessageDescriptor,
  SelfHelpFeedbackTicketContext,
} from "../interfaces/api";
import type { SelfHelpFeedbackState } from "../interfaces/selfHelpFeedback";
import {
  createSelfHelpFeedbackState,
  selfHelpFeedbackReducer,
} from "./selfHelpFeedbackState";

export type SelfHelpFeedbackAbandonReason =
  | "unrelated-query"
  | "reset"
  | "session-expired"
  | "navigation";

export interface SelfHelpFeedbackJourneyEvent {
  journeyId: string;
  selfHelpTurnId: string;
  reason: SelfHelpFeedbackAbandonReason;
}

export type SelfHelpFeedbackJourneyEventDetail =
  | ({ type: "abandoned" } & SelfHelpFeedbackJourneyEvent)
  | {
      type:
        | "resolved"
        | "ticket-requested"
        | "ticket-form-rendered"
        | "ticket-submission-succeeded"
        | "ticket-submission-failed"
        | "ticket-bypass-blocked";
      journeyId: string;
      selfHelpTurnId: string;
      category?: import("../interfaces/selfHelpFeedback").SelfHelpFeedbackCategory;
      validatorFallback?: boolean;
      clarificationUsed?: boolean;
      primaryFailureReason?: import("../interfaces/api").SelfHelpFeedbackPrimaryFailureReason;
    };

export interface ActiveSelfHelpFeedbackJourney {
  messageId: string;
  descriptor: SelfHelpFeedbackMessageDescriptor;
  state: SelfHelpFeedbackState;
}

const PENDING_SUBMISSION_STORAGE_KEY =
  "mcapsCopilot_selfHelpFeedbackPendingSubmission";

interface PendingSelfHelpFeedbackSubmission {
  sessionId: string;
  association: SelfHelpFeedbackFormAssociation;
}

export const persistPendingSelfHelpFeedbackSubmission = (
  sessionId: string,
  association: SelfHelpFeedbackFormAssociation
): void => {
  try {
    sessionStorage.setItem(
      PENDING_SUBMISSION_STORAGE_KEY,
      JSON.stringify({ sessionId, association })
    );
  } catch {
    // Ticket creation must continue when browser storage is unavailable.
  }
};

export const getPendingSelfHelpFeedbackSubmission = (
  sessionId: string
): SelfHelpFeedbackFormAssociation | undefined => {
  try {
    const persisted = JSON.parse(
      sessionStorage.getItem(PENDING_SUBMISSION_STORAGE_KEY) || "null"
    ) as PendingSelfHelpFeedbackSubmission | null;
    return persisted?.sessionId === sessionId
      ? persisted.association
      : undefined;
  } catch {
    return undefined;
  }
};

export const clearPendingSelfHelpFeedbackSubmission = (
  sessionId: string
): void => {
  try {
    const persisted = JSON.parse(
      sessionStorage.getItem(PENDING_SUBMISSION_STORAGE_KEY) || "null"
    ) as PendingSelfHelpFeedbackSubmission | null;
    if (!persisted || persisted.sessionId === sessionId) {
      sessionStorage.removeItem(PENDING_SUBMISSION_STORAGE_KEY);
    }
  } catch {
    try {
      sessionStorage.removeItem(PENDING_SUBMISSION_STORAGE_KEY);
    } catch {
      // Ticket creation must continue when browser storage is unavailable.
    }
  }
};

const isTerminal = (state: SelfHelpFeedbackState): boolean =>
  state.status === "resolved" || state.status === "abandoned";

export const isIncompleteSelfHelpFeedback = (
  state: SelfHelpFeedbackState
): boolean =>
  state.status === "resolution" ||
  state.status === "category" ||
  state.status === "feedback" ||
  state.status === "validating" ||
  state.status === "revision" ||
  state.status === "ticket-ready" ||
  state.status === "fallback";

const getMessageJourney = (
  message: Message
): ActiveSelfHelpFeedbackJourney | null => {
  const descriptor =
    message.meta?.selfHelpFeedback ||
    message.meta?.dataToDisplay?.selfHelpFeedback;
  if (!descriptor) {
    return null;
  }

  const persistedState =
    message.meta?.selfHelpFeedbackState ||
    createSelfHelpFeedbackState(
      {
        descriptorId: descriptor.descriptorId,
        owningSelfHelpTurnId: descriptor.owningSelfHelpTurnId,
      },
      () => descriptor.journeyId
    );

  return {
    messageId: message.id,
    descriptor,
    state: persistedState,
  };
};

export const findActiveSelfHelpFeedbackJourney = (
  messages: Message[]
): ActiveSelfHelpFeedbackJourney | null => {
  for (let index = messages.length - 1; index >= 0; index--) {
    const journey = getMessageJourney(messages[index]);
    if (journey && !isTerminal(journey.state)) {
      return journey;
    }
  }

  return null;
};

export const hasRestorableSelfHelpFeedbackJourney = (
  messages: Message[] | null | undefined
): boolean =>
  Array.isArray(messages) &&
  findActiveSelfHelpFeedbackJourney(messages) !== null;

export const shouldRestoreSelfHelpFeedbackHistory = (
  navigationType: PerformanceNavigationTiming["type"] | undefined,
  messages: Message[] | null | undefined
): boolean =>
  navigationType === "reload" &&
  hasRestorableSelfHelpFeedbackJourney(messages);

export const buildSelfHelpFeedbackTicketContext = (
  descriptor: SelfHelpFeedbackMessageDescriptor,
  state: SelfHelpFeedbackState
): SelfHelpFeedbackTicketContext => {
  if (
    (state.status !== "ticket-ready" && state.status !== "fallback") ||
    !("category" in state) ||
    !("feedback" in state)
  ) {
    throw new Error("Self-help feedback is not ready for ticket handoff.");
  }

  return {
    journeyId: state.journeyId,
    selfHelpTurnId: descriptor.selfHelpTurnId,
    category: state.category,
    feedback: state.feedback,
    guidanceReference: descriptor.guidanceReference,
    outcome: "stillNeedsHelp",
    validatorFallback: state.status === "fallback",
    clarificationUsed:
      "validatorGuidance" in state &&
      typeof state.validatorGuidance === "string" &&
      state.validatorGuidance.length > 0,
    ...("primaryFailureReason" in state && state.primaryFailureReason
      ? { primaryFailureReason: state.primaryFailureReason }
      : {}),
  };
};

export const buildSelfHelpFeedbackFormAssociation = (
  context: SelfHelpFeedbackTicketContext
): SelfHelpFeedbackFormAssociation => ({
  journeyId: context.journeyId,
  selfHelpTurnId: context.selfHelpTurnId,
  category: context.category,
  validatorFallback: context.validatorFallback,
  clarificationUsed: context.clarificationUsed,
  primaryFailureReason: context.primaryFailureReason,
});

export const abandonIncompleteSelfHelpFeedback = (
  messages: Message[],
  reason: SelfHelpFeedbackAbandonReason
): { messages: Message[]; abandoned: SelfHelpFeedbackJourneyEvent[] } => {
  const abandoned: SelfHelpFeedbackJourneyEvent[] = [];
  let changed = false;

  const nextMessages = messages.map((message) => {
    const journey = getMessageJourney(message);
    if (!journey || !isIncompleteSelfHelpFeedback(journey.state)) {
      return message;
    }

    const nextState = selfHelpFeedbackReducer(journey.state, {
      type: "abandon",
    });
    if (nextState === journey.state) {
      return message;
    }

    changed = true;
    abandoned.push({
      journeyId: journey.state.journeyId,
      selfHelpTurnId: journey.descriptor.selfHelpTurnId,
      reason,
    });
    return {
      ...message,
      meta: {
        ...message.meta,
        selfHelpFeedbackState: nextState,
      },
    };
  });

  return {
    messages: changed ? nextMessages : messages,
    abandoned,
  };
};

export const completeSelfHelpFeedbackJourney = (
  messages: Message[],
  association: Pick<
    SelfHelpFeedbackFormAssociation,
    "journeyId" | "selfHelpTurnId"
  >
): Message[] => {
  let changed = false;
  const nextMessages = messages.map((message) => {
    const journey = getMessageJourney(message);
    if (
      !journey ||
      journey.state.journeyId !== association.journeyId ||
      journey.descriptor.selfHelpTurnId !== association.selfHelpTurnId
    ) {
      return message;
    }

    const nextState = selfHelpFeedbackReducer(journey.state, {
      type: "complete",
    });
    if (nextState === journey.state) {
      return message;
    }

    changed = true;
    return {
      ...message,
      meta: {
        ...message.meta,
        selfHelpFeedbackState: nextState,
      },
    };
  });

  return changed ? nextMessages : messages;
};

export const isDirectTicketIntent = (query: string): boolean => {
  const normalized = query.trim();
  if (
    /\b(?:CAP-\d+|CAS-\d+|HR-\d+|INC\d{8}|TKT-\d{7}-[A-Z0-9]{5}|\d-\d{13})\b/i.test(
      normalized
    ) ||
    /\b(?:get|find|show|check|track|view|look\s*up)\b[\s\S]{0,40}\b(?:my|our|the)\s+(?:(?:INC|HRD|TKT|Compete)\s+)?(?:tickets?|incidents?|support requests?|requests?)\b/i.test(
      normalized
    ) ||
    /\b(?:get|find|show|check|track|view|look\s*up)\b[\s\S]{0,40}\b(?:tickets?|incidents?|support requests?)\s+(?:status|details|updates?)\b/i.test(
      normalized
    ) ||
    /^(how\s+(?:do|can|should)\s+i|what\s+is|show\s+me|where\s+can\s+i|can\s+you\s+explain)\b/i.test(
      normalized
    )
  ) {
    return false;
  }

  return (
    /\b(create|open|raise|file|submit|start|log|get|request)\b[\s\S]{0,40}\b(ticket|incident|support request)\b/i.test(
      normalized
    ) ||
    /\b(?:i|we)\s+(?:need|want|require)\b[\s\S]{0,30}\b(ticket|incident|support request)\b/i.test(
      normalized
    )
  );
};

export const emitSelfHelpFeedbackJourneyEvent = (
  event: SelfHelpFeedbackJourneyEventDetail
): void => {
  window.dispatchEvent(
    new CustomEvent("copilot-self-help-journey-event", {
      detail: event,
    })
  );
};

// Generated by Copilot
// updated on: 2026-09-09 | Story 1297509 TASK-009 | Added pure journey persistence, handoff, abandonment, and anti-bypass helpers | Time Taken 10 min
// updated on: 2026-09-09 | Story 1297509 TASK-010 | Centralized the resolved journey event seam for telemetry | Time Taken 2 min
// updated on: 2026-09-09 | Story 1297509 UI audit | Added safe form association and submission outcome event types | Time Taken 3 min
// updated on: 2026-09-09 | Story 1297509 UI audit | Covered common direct-ticket phrasing without misclassifying lookup questions | Time Taken 3 min
// updated on: 2026-09-14 | 7 lines changed | [Auto Generated] | Carry validated failure reason through ticket and telemetry events | Time Taken 2 min
// updated on: 2026-09-18 | 2 lines added | [Auto Generated] | Preserve category in form telemetry association for AC-27 | Time Taken 1 min
// updated on: 2026-09-18 | 48 lines added | [Auto Generated] | Persist privacy-safe feedback association across conversational form confirmation | Time Taken 5 min
// updated on: 2026-10-18 | 45 lines changed | [Auto Generated] | Complete journeys and exclude ID and possessive ticket lookups from create handoff | Time Taken 5 min
